import i18next from 'i18next';

import { roundWithDecimals } from '@commons/utils/math';

import { getChannelLabel, getCompositionLabel } from './getProductExportLabels';
import { getProductPriceExcludingVAT } from '../detail/components/ProductInfo/utils/getProductPriceExcludingVAT';

/**
 * Display label for a product's type (parent / child), null when neither applies.
 *
 * @param {Object} product
 * @returns {string|null}
 */
export const getProductType = (product) => {
  if (product.isChild) {
    return i18next.t('ADMIN.PRODUCTS.PRODUCT_TYPE_CHILD');
  }

  if (product.isParent) {
    return i18next.t('ADMIN.PRODUCTS.PRODUCT_TYPE_MAIN');
  }

  return null;
};

/**
 * Enrich a product (in place) with the display fields shared by every product export row:
 * status, category, sub-category, type, brand name, default price excluding VAT and composition.
 *
 * @param {Object} product
 * @param {Object[]} clientBrands
 * @param {string} defaultChannelId
 * @returns {Object} The same product, enriched
 */
export const prepareProductForExport = (product, clientBrands, defaultChannelId) => {
  product.status = product.active ? i18next.t('GENERAL.ACTIVE') : i18next.t('GENERAL.INACTIVE');
  product.category = product.category || i18next.t('GENERAL.NONE_VALUE');
  product.subCategory = product.subCategory || i18next.t('GENERAL.NONE_VALUE');
  product.type = getProductType(product);

  if (product.brandId) {
    const matchingBrand = (clientBrands || []).find(({ id }) => id === product.brandId);

    product.brandName = matchingBrand?.name || '';
  }

  product.priceHT = getProductPriceExcludingVAT(product, defaultChannelId) || '';
  product.composition = getCompositionLabel(product.lnkEntityProductrel);

  return product;
};

/**
 * Build a single product export row for a given recipe cost (main recipe or a variant) on a given
 * channel, recomputing the per-channel price, cost ratio and margin.
 *
 * @param {Object} product - Product already prepared by prepareProductForExport
 * @param {Object} recipe - The recipe (or recipe-channel) being exported
 * @param {Object} channel - The matching channel object
 * @param {string} channelId - The resolved channel id
 * @param {number} rawTotalCost - The recipe total cost to use for this row
 * @param {Object} [extraRecipeFields] - Extra fields merged into the row's recipe (variantName, …)
 * @returns {{ product: Object, recipe: Object }}
 */
export const buildProductRecipeRow = (
  product,
  recipe,
  channel,
  channelId,
  rawTotalCost,
  extraRecipeFields = {},
) => {
  const totalCost = roundWithDecimals(rawTotalCost, 4);
  const formattedPriceHT = getProductPriceExcludingVAT(product, channelId) || '';

  const totalRatio =
    formattedPriceHT && totalCost ? roundWithDecimals((totalCost / formattedPriceHT) * 100, 4) : '';

  const marginRatio =
    formattedPriceHT && totalCost
      ? roundWithDecimals(((formattedPriceHT - totalCost) / formattedPriceHT) * 100, 4)
      : '';

  const channelPrice = product.channelPrices?.[channelId];

  return {
    product: {
      ...product,
      priceHT: formattedPriceHT,
      vatRate: channelPrice?.vatRate,
      priceWithTaxes: channelPrice?.priceWithTaxes,
    },
    recipe: {
      ...recipe,
      marginRatio,
      recipeCost: totalCost,
      ratioCost: totalRatio,
      channel: getChannelLabel(recipe, channel),
      ...extraRecipeFields,
    },
  };
};
